import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Switch,
} from "@my-saas/ui";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import {
  useAlertMessageMutations,
  useAlertMessages,
} from "@/client/services/tenant-config";

const LANGS = [
  { code: "he", label: "Hebrew" },
  { code: "en", label: "English" },
] as const;

/**
 * Alert messages editor — list + create + per-language text editor.
 * Banner upload is deferred to a follow-up; for now the bannerPath field
 * accepts a URL or relative path string.
 */
export const AlertMessagesPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: messages, isPending } = useAlertMessages(tenantSlug);
  const { create, update, remove, setLang } = useAlertMessageMutations(tenantSlug);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");

  const handleCreate = async () => {
    if (!newName.trim() || !newId.trim()) return;
    await create.mutateAsync({ id: newId.trim(), name: newName.trim() });
    setNewId("");
    setNewName("");
    toast({ title: "Alert message created" });
  };

  return (
    <>
      <Helmet>
        <title>Alert messages · {tenantSlug}</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Alert messages</h1>
          <p className="text-sm text-muted-foreground">
            Message templates dispatched when an OREF alert matches a tenant
            subscription. Per language (Hebrew + English), with an optional
            banner image.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create alert message</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr,2fr,auto]"
              onSubmit={(e) => {
                e.preventDefault();
                void handleCreate();
              }}
            >
              <Input
                placeholder="id (e.g. rocket)"
                value={newId}
                onChange={(e) => setNewId(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, "-"))}
              />
              <Input
                placeholder="display name (e.g. Rocket attack — Tel Aviv)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button type="submit" disabled={!newName.trim() || !newId.trim() || create.isPending}>
                <PlusIcon className="mr-2 size-4" /> Create
              </Button>
            </form>
          </CardContent>
        </Card>

        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

        {messages?.map((m) => (
          <AlertMessageCard
            key={m.id}
            message={m}
            onPatch={(patch) => update.mutate({ id: m.id, ...patch })}
            onRemove={() => {
              if (confirm(`Delete "${m.name}"?`)) remove.mutate(m.id);
            }}
            onSaveLang={(langCode, text) =>
              setLang.mutate({ id: m.id, langCode, messageText: text })
            }
          />
        ))}
      </div>
    </>
  );
};

interface CardProps {
  message: {
    id: string;
    name: string;
    category: string;
    bannerPath: string;
    addDatetime: boolean;
    enabled: boolean;
    sortOrder: number;
    langs: { langCode: string; messageText: string }[];
  };
  onPatch: (patch: Partial<{ name: string; category: string; bannerPath: string; addDatetime: boolean; enabled: boolean; sortOrder: number }>) => void;
  onRemove: () => void;
  onSaveLang: (langCode: string, messageText: string) => void;
}

function AlertMessageCard({ message, onPatch, onRemove, onSaveLang }: CardProps) {
  const [name, setName] = useState(message.name);
  const [category, setCategory] = useState(message.category);
  const [bannerPath, setBannerPath] = useState(message.bannerPath);
  const [langText, setLangText] = useState<Record<string, string>>({});

  useEffect(() => {
    setLangText(
      Object.fromEntries(message.langs.map((l) => [l.langCode, l.messageText])),
    );
  }, [message]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <span>{message.name}</span>
            <Badge variant="secondary" className="font-mono text-xs">
              {message.id}
            </Badge>
            {!message.enabled && <Badge variant="secondary">disabled</Badge>}
          </CardTitle>
          <CardDescription className="text-xs">{message.langs.length} language(s) defined</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={message.enabled}
            onCheckedChange={(v) => onPatch({ enabled: v })}
          />
          <Button size="sm" variant="error" onClick={onRemove}>
            <TrashIcon className="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== message.name && onPatch({ name })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} onBlur={() => category !== message.category && onPatch({ category })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Banner URL / path</Label>
            <Input value={bannerPath} onChange={(e) => setBannerPath(e.target.value)} onBlur={() => bannerPath !== message.bannerPath && onPatch({ bannerPath })} />
          </div>
        </div>

        <Tabs defaultValue="he" className="w-full">
          <TabsList>
            {LANGS.map((l) => (
              <TabsTrigger key={l.code} value={l.code}>
                {l.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {LANGS.map((l) => (
            <TabsContent key={l.code} value={l.code} className="space-y-2">
              <textarea
                className="w-full min-h-[120px] rounded border bg-background p-2 text-sm"
                dir={l.code === "he" ? "rtl" : "ltr"}
                placeholder={l.code === "he" ? "תוכן ההודעה בעברית…" : "Message body in English…"}
                value={langText[l.code] ?? ""}
                onChange={(e) =>
                  setLangText((prev) => ({ ...prev, [l.code]: e.target.value }))
                }
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSaveLang(l.code, langText[l.code] ?? "")}
              >
                Save {l.label}
              </Button>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
